import { AlertTriangle } from 'lucide-react';

import { List } from '@/shared/components/list/list';
import { Alert, AlertDescription } from '@/shared/components/shadcn/alert';
import { Button } from '@/shared/components/shadcn/button';
import { CampaignCard } from './campaign-card';
import { CampaignStatusTabs, SearchToolbar, SortControl } from './campaigns-page-controls';
import { CampaignListLoading, EmptyState, FilteredEmptyState } from './campaigns-page-states';
import { CampaignsPageHeader } from './campaigns.header';

import { useCampaignSlot } from '@/entities/campaign/query/use-campaign-slot';
import { OverlayBanner } from '@/shared/components/overlay-banner';
import { Scene, Stage } from '@/shared/components/stage/stage';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useNavigate } from '@tanstack/react-router';
import { getNudgeBannerContent } from '../../helpers/nudge-banner-content';
import { useCampaignList } from '../../hooks/use-campaigns-list';
import { useDeleteCampaignRequest } from '../../overlays/use-delete-campaign-request';
import { useEndCampaignRequest } from '../../overlays/use-end-campaign-request';
import { useRenameCampaignRequest } from '../../overlays/use-rename-campaign-request';

export function CampaignsPage() {
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const navigate = useNavigate();

  const { campaignsQuery, isEmpty, isFilteredEmpty, filters } = useCampaignList();
  const campaigns = campaignsQuery.data?.pages.flatMap((pageData) => pageData.items) ?? [];
  const campaignSlotQuery = useCampaignSlot();
  const nudgeContent = getNudgeBannerContent(
    workspace.plan,
    workspace.constraints.campaign_slot_limit,
  );

  const deleteCampaignRequest = useDeleteCampaignRequest();
  const endCampaignRequest = useEndCampaignRequest();
  const renameCampaignRequest = useRenameCampaignRequest();

  const activeCount = campaignSlotQuery.data?.count || 0;
  const campaignCountLimit = workspace?.constraints.campaign_slot_limit ?? 50;
  const remainingCount = Math.max(campaignCountLimit - activeCount, 0);
  const isCreateDisabled = campaignsQuery.isSuccess && remainingCount === 0;

  const goToCreateCampaign = () => {
    navigate({
      to: '/workspace/$workspaceId/campaigns/create',
      params: { workspaceId },
    });
  };

  const goToCampaignDashboard = (campaignId: string) => {
    navigate({
      to: '/workspace/$workspaceId/campaigns/$campaignId',
      params: { workspaceId, campaignId },
    });
  };

  return (
    <div className="flex h-full flex-col">
      <CampaignsPageHeader
        activeOrScheduledCount={activeCount}
        isCreateDisabled={isCreateDisabled}
        planCampaignLimit={workspace.constraints.campaign_slot_limit}
        onCreateCampaign={goToCreateCampaign}
      />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6">
        <section className="space-y-4">
          <CampaignStatusTabs
            selectedFilter={filters.statusFilter}
            onSelect={filters.setStatusFilter}
          />

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <SearchToolbar
              searchQuery={filters.searchQuery}
              onSearchQueryChange={filters.setSearchQuery}
            />

            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm font-medium">Sort by</span>
              <SortControl
                sortOption={filters.sortOption}
                onSortOptionChange={filters.setSortOption}
              />
            </div>
          </div>
        </section>

        <section className="pb-8">
          <Stage>
            <Scene status={campaignsQuery.isLoading}>
              <CampaignListLoading />
            </Scene>
            <Scene status={campaignsQuery.isError}>
              <Alert className="border-warning/30 bg-warning/10">
                <AlertTriangle className="text-warning size-4" />
                <AlertDescription className="text-warning space-y-4 text-sm">
                  <p>
                    Campaign data could not be loaded right now. Retry the request or create a new
                    campaign after the API becomes available again.
                  </p>
                  <div>
                    <Button variant="outline" onClick={() => campaignsQuery.refetch()}>
                      Retry
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </Scene>
            <Scene status={isEmpty}>
              <EmptyState onCreate={goToCreateCampaign} />
            </Scene>
            <Scene status={isFilteredEmpty}>
              <FilteredEmptyState
                searchQuery={filters.searchQuery}
                selectedFilter={filters.statusFilter}
              />
            </Scene>
            <Scene default>
              <List
                className="grid gap-4 overflow-visible"
                data={campaigns}
                isLoading={campaignsQuery.isRefetching}
                hasNextPage={campaignsQuery.hasNextPage}
                fetchNextPage={() => {
                  void campaignsQuery.fetchNextPage();
                }}
                isFetchingNextPage={campaignsQuery.isFetchingNextPage}
                render={(campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    onDelete={() => deleteCampaignRequest(campaign.id, campaign.name)}
                    onEnd={() => endCampaignRequest(campaign.id, campaign.name)}
                    onOpen={() => goToCampaignDashboard(campaign.id)}
                    onRename={() => renameCampaignRequest(campaign.id, campaign.name)}
                  />
                )}
              />
            </Scene>
          </Stage>
          <ToggleStage status={isCreateDisabled} className="mt-10">
            <OverlayBanner
              title={nudgeContent.title}
              description={
                <>
                  {nudgeContent.body}
                  <br />
                  {nudgeContent.supportingBody}
                </>
              }
              action={
                <Button
                  size="sm"
                  variant="default"
                  className="w-full shrink-0 sm:w-auto"
                  onClick={() => navigate({ from: nudgeContent.href })}
                >
                  {nudgeContent.cta}
                </Button>
              }
              className="relative"
            />
          </ToggleStage>
        </section>
      </div>
    </div>
  );
}
